import supabase from '../supabaseClient';
import { generateJoinCode } from '../utils/joinCode';
import { parseMissingCompetitionColumn } from './competitionInsert';

export const ACADEMY_LEAGUE_JOIN_STORAGE_KEY = 'statbox:academyLeague:joinToken';
export const ACADEMY_LEAGUE_JOIN_PATH = '/join/league';
const PRODUCTION_ORIGIN = 'https://www.statbox.co';

function getOrigin() {
  if (typeof window !== 'undefined') return window.location.origin;
  return PRODUCTION_ORIGIN;
}

export function generateAcademyLeagueJoinToken() {
  return `${generateJoinCode()}${generateJoinCode()}`.slice(0, 12);
}

export function buildAcademyLeagueJoinPath(token) {
  if (!token) return '';
  return `${ACADEMY_LEAGUE_JOIN_PATH}/${encodeURIComponent(token)}`;
}

export function buildAcademyLeagueJoinUrl(token) {
  if (!token) return '';
  return `${getOrigin()}${buildAcademyLeagueJoinPath(token)}`;
}

export function buildAcademyLeagueInviteWhatsAppText(leagueName, joinUrl) {
  const name = leagueName || 'a Statbox academy league';
  return `You're invited to join ${name} on Statbox. If you don't have an academy yet, create one from this link, then pick a squad: ${joinUrl}`;
}

export function stashAcademyLeagueJoinToken(token) {
  const value = String(token || '').trim();
  if (!value || typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(ACADEMY_LEAGUE_JOIN_STORAGE_KEY, value);
  } catch {
    /* ignore */
  }
}

export function peekAcademyLeagueJoinToken() {
  if (typeof window === 'undefined') return '';
  try {
    return String(sessionStorage.getItem(ACADEMY_LEAGUE_JOIN_STORAGE_KEY) || '').trim();
  } catch {
    return '';
  }
}

export function clearAcademyLeagueJoinToken() {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.removeItem(ACADEMY_LEAGUE_JOIN_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export async function previewAcademyLeagueInvite(token) {
  const trimmed = String(token || '').trim();
  if (!trimmed) return { ok: false, error_code: 'invalid_token' };
  const { data, error } = await supabase.rpc('preview_academy_league_invite', { p_token: trimmed });
  if (error) {
    console.warn('previewAcademyLeagueInvite failed:', error);
    return { ok: false, error_code: 'invalid_token', error: error.message };
  }
  return data || { ok: false, error_code: 'invalid_token' };
}

export async function claimAcademyLeagueInvite(token) {
  const trimmed = String(token || '').trim();
  if (!trimmed) return { ok: false, error_code: 'invalid_token' };
  const { data, error } = await supabase.rpc('claim_academy_league_invite', { p_token: trimmed });
  if (error) {
    console.warn('claimAcademyLeagueInvite failed:', error);
    return { ok: false, error_code: 'claim_failed', error: error.message };
  }
  return data || { ok: false, error_code: 'claim_failed' };
}

export async function createAcademyAndClaimLeagueInvite({ token, name, region, isPrivate = false }) {
  const trimmedToken = String(token || '').trim();
  const trimmedName = String(name || '').trim();
  if (!trimmedToken) return { ok: false, error_code: 'invalid_token' };
  if (!trimmedName) return { ok: false, error_code: 'name_required' };
  const { data, error } = await supabase.rpc('create_academy_and_claim_league_invite', {
    p_token: trimmedToken,
    p_name: trimmedName,
    p_region: String(region || '').trim() || null,
    p_is_private: isPrivate === true,
  });
  if (error) {
    console.warn('createAcademyAndClaimLeagueInvite failed:', error);
    return { ok: false, error_code: 'create_failed', error: error.message };
  }
  return data || { ok: false, error_code: 'create_failed' };
}

export async function ensureAcademyLeagueJoinToken(competition) {
  if (!competition?.id) return competition;
  if (competition.participant_type !== 'academy' || competition.format !== 'league') {
    return competition;
  }
  if (competition.academy_join_token) return competition;

  const token = generateAcademyLeagueJoinToken();
  const { data, error } = await supabase
    .from('competitions')
    .update({ academy_join_token: token })
    .eq('id', competition.id)
    .select('academy_join_token')
    .maybeSingle();

  if (error) {
    if (parseMissingCompetitionColumn(error) === 'academy_join_token') {
      console.warn('academy_join_token column is missing. Run academy_league_join_token.sql.');
      return competition;
    }
    console.warn('ensureAcademyLeagueJoinToken failed:', error);
    return competition;
  }

  return {
    ...competition,
    academy_join_token: data?.academy_join_token || token,
  };
}

export function academyLeagueJoinErrorMessage(result) {
  const code = result?.error_code;
  if (code === 'invalid_token') return 'This invite link is invalid or expired.';
  if (code === 'not_authenticated') return 'Sign in to continue.';
  if (code === 'no_academy') return 'Create your academy first, then you can join this league.';
  if (code === 'league_full') return 'This league is already full.';
  if (code === 'sport_mismatch') return 'Your academy sport does not match this league.';
  if (code === 'name_required') return 'Enter an academy name.';
  if (code === 'create_failed') return 'Could not create the academy. Try again.';
  return result?.error || 'Could not join this league. Try again.';
}

/** Minimal Users row after OAuth — name + email only; academy is created on the join page next. */
export async function ensureLeagueJoinUserProfile(authUser) {
  if (!authUser?.id) return null;

  const { data: existing } = await supabase
    .from('Users')
    .select('user_id, name, email')
    .eq('user_id', authUser.id)
    .maybeSingle();

  if (existing) return existing;

  const meta = authUser.user_metadata || {};
  const name = String(meta.full_name || meta.name || authUser.email?.split('@')[0] || 'Coach').trim();
  const email = authUser.email?.trim() || `${authUser.id}@oauth.statbox.local`;

  const { data, error } = await supabase
    .from('Users')
    .insert([{ user_id: authUser.id, name, email, coach: 'true' }])
    .select('user_id, name, email')
    .maybeSingle();

  if (error) {
    console.warn('ensureLeagueJoinUserProfile failed:', error);
    return null;
  }
  return data;
}
